import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import styled from 'styled-components';
import {Formik} from 'formik';
import * as yup from 'yup';
import Select from 'react-select';
import {Col, Modal, Row} from 'react-bootstrap';
import {NotificationManager} from 'react-notifications';
import {hideLoader, showLoader} from '../../../store/common/slice';
import {StyledModal} from '../../shared/modal.styled';
import {BllinkLogger} from '../../utils/bllink_loggers';
import { constants } from '../../constants/constants';
import {GAEvent} from '../../google_analytics/google_analytics';
import {selectCustomStyles} from '../../shared/select.styled';
import {useAccessToken} from '../../hooks/useAccessToken';

const StyledFastOneTime = styled.div`
  margin-bottom: 40px;

  .add-new {
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    text-decoration: underline;
    color: var(--bllink-purple);
    font-weight: bold;
    font-size: 18px;
    cursor: pointer;
  }
`;

export default function AddFastPayment({buildingID, apartmentNumber}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();

    const [showModal, setShowModal] = useState(false);

    const validationSchema = yup.object().shape({
        name: yup.string(),
        note: yup.string(),
        instalments: yup.object(),
        amount: yup.string().matches(/^-?\d+\.?\d*$/, t('titles.forms.number_field'))
    });
    const initialFormValues = {
        name: '',
        amount: '',
        installments: {
            value: 1,
            label: 1
        },
        note: '',
    };

    const createOneTimePayment = async (evt, data) => {
        evt.preventDefault();
        dispatch(showLoader());
        let payload = {
            amount: parseFloat(data.amount),
            description: data.name,
            longDescription: data.note,
            numberOfInstallments: data.installments.value,
            apartmentNumbers: [apartmentNumber]
        };
        let response = await client.put(`/managers/onetime-payments/${buildingID}`, payload);
        BllinkLogger.info(`response fast one-time payment`, response);

        if (response.length) {
            NotificationManager.success(t('payment_settings.oneTimeSuccess', {payments: response.length}), '', 5000);
            GAEvent(constants.GoogleAnalytics.Events.fastOneTime, 'createFastOneTimePayment', payload, 'createFastOneTimePaymentSuccess');
            setShowModal(false);
            setTimeout(() =>
                window.location.reload(),
                2000);
        } else {
            NotificationManager.error(t('payment_settings.oneTimeError'), '', 5000);
        }

        dispatch(hideLoader());
    }

    const getInstallmentsOptions = () => {
        return Array.from({length: 12}, (v, k) => k+1).map(item => ({label: item, value: item}));
    }

    return (
        <StyledFastOneTime>
            <div
                className={'add-new'}
                onClick={() => setShowModal(true)}
            >
                <span className={'plus'}>+</span>
                {t('payment_settings.addNew')}
            </div>
            <StyledModal
                centered
                dialogClassName="fast-one-time-modal"
                show={showModal}
                onHide={() => setShowModal(false)}
            >
            <span
                className={'modal-close'}
                onClick={() => setShowModal(false)}
            >
                &#215;
            </span>
                <Modal.Body>
                    <h3>{t('payment_settings.addNew')}</h3>
                    <Formik
                        initialValues={initialFormValues}
                        validationSchema={validationSchema}
                    >
                        {({
                              errors,
                              touched,
                              values,
                              handleChange,
                              handleBlur,
                              isValid,
                              dirty,
                              setFieldValue,
                              setFieldTouched
                        }) => (
                            <form>
                                <Row>
                                    <Col>
                                        <div className="form-group">
                                            <label htmlFor="name">{t('payment_settings.name')} </label>
                                            <input
                                                type="text"
                                                className={`form-control bllink-input-border ${errors.name && touched.name ? 'error' : ''}`}
                                                name="name"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.name}
                                            />
                                            {
                                                errors.name && touched.name && (
                                                    <label className={'error'}>{errors.name}</label>
                                                )
                                            }
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="form-group">
                                            <label htmlFor="amount">{t('payment_settings.amount')} </label>
                                            <input
                                                type="text"
                                                className={`form-control bllink-input-border ${errors.amount && touched.amount ? 'error' : ''}`}
                                                name="amount"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.amount}
                                            />
                                            {
                                                errors.amount && touched.amount && (
                                                    <label className={'error'}>{errors.amount}</label>
                                                )
                                            }
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="form-group">
                                            <label htmlFor="installments">{t('payment_settings.installments')} </label>
                                            <Select
                                                styles={selectCustomStyles}
                                                options={getInstallmentsOptions()}
                                                onChange={num => setFieldValue('installments', num)}
                                                onBlur={() => setFieldTouched('installments', true)}
                                                value={values.installments}
                                                name="installments"
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <div className="form-group">
                                            <label htmlFor="comment">{t('payment_settings.note')} </label>
                                            <input
                                                type="text"
                                                className={`form-control bllink-input-border ${errors.note && touched.note ? 'error' : ''}`}
                                                name="note"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.note}
                                            />
                                            {
                                                errors.note && touched.note && (
                                                    <label className={'error'}>{errors.note}</label>
                                                )
                                            }
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="justify-content-center mt-3">
                                    <Col sm={'auto'}>
                                        <button
                                            type="submit"
                                            className="bllink-button"
                                            disabled={!isValid}
                                            onClick={(evt) => createOneTimePayment(evt, values)}
                                        >
                                            {t('payment_settings.createPayment')}
                                        </button>
                                    </Col>
                                </Row>
                            </form>
                        )}
                    </Formik>
                </Modal.Body>
            </StyledModal>
        </StyledFastOneTime>
    )
}
